/**
 * KpiCard — compact KPI summary card with a big number, optional trend
 * indicator, and optional metric pill row.
 *
 * Use when: building a dashboard header, SLA report, or fleet overview.
 * For a single number badge inline use `MetricPill`. For empty/error states
 * inside the card body, render `EmptyState` as `children`.
 */
import type { ReactNode } from 'react';
import { Body1, Caption1, Card, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { ArrowTrendingRegular, ArrowTrendingDownRegular } from '@fluentui/react-icons';

type KpiTone = 'neutral' | 'success' | 'warning' | 'danger' | 'brand';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  title: {
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
  },
  value: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightHero700,
  },
  unit: {
    color: tokens.colorNeutralForeground3,
  },
  trend: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  trendUp: { color: tokens.colorPaletteGreenForeground1 },
  trendDown: { color: tokens.colorPaletteRedForeground1 },
  trendFlat: { color: tokens.colorNeutralForeground3 },
  /* Tone accents shift the value colour */
  tNeutral: { color: tokens.colorNeutralForeground1 },
  tSuccess: { color: tokens.colorPaletteGreenForeground1 },
  tWarning: { color: tokens.colorPaletteYellowForeground1 },
  tDanger:  { color: tokens.colorPaletteRedForeground1 },
  tBrand:   { color: tokens.colorBrandForeground1 },
  footer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
  },
});

interface KpiCardProps {
  /** Small uppercase label above the value. */
  title: string;
  /** Primary number/string. */
  value: string | number;
  /** Optional unit suffix (e.g. "%", "kWh"). */
  unit?: string;
  /** Optional trend value & direction. Positive = up, negative = down. */
  trend?: { value: string | number; direction: 'up' | 'down' | 'flat' };
  /** Tone affects the colour of the primary value. */
  tone?: KpiTone;
  /** Optional caption line under the value. */
  caption?: ReactNode;
  /** Optional footer slot — typically a row of MetricPills or a ProgressBar. */
  footer?: ReactNode;
}

const TONE_CLASS: Record<KpiTone, keyof ReturnType<typeof useStyles>> = {
  neutral: 'tNeutral',
  success: 'tSuccess',
  warning: 'tWarning',
  danger: 'tDanger',
  brand: 'tBrand',
};

export function KpiCard({ title, value, unit, trend, tone = 'neutral', caption, footer }: KpiCardProps) {
  const styles = useStyles();
  const valueClass = mergeClasses(styles.value, styles[TONE_CLASS[tone]]);

  return (
    <Card>
      <div className={styles.root}>
        <div className={styles.header}>
          <Caption1 className={styles.title}>{title}</Caption1>
          {trend ? (
            <span
              className={mergeClasses(
                styles.trend,
                trend.direction === 'up' && styles.trendUp,
                trend.direction === 'down' && styles.trendDown,
                trend.direction === 'flat' && styles.trendFlat,
              )}
            >
              {trend.direction === 'up' ? <ArrowTrendingRegular /> : trend.direction === 'down' ? <ArrowTrendingDownRegular /> : null}
              {trend.value}
            </span>
          ) : null}
        </div>
        <div className={styles.valueRow}>
          <span className={valueClass}>{value}</span>
          {unit ? <Body1 className={styles.unit}>{unit}</Body1> : null}
        </div>
        {caption ? <Caption1 className={styles.unit}>{caption}</Caption1> : null}
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </Card>
  );
}
